/**
 * ADR-074 §3.5 / §11 — Email Validation, the OUTBOUND sub-tab. One two-row card
 * per outbound EmailComm: metadata columns on row 1 (locked widths per §11), the
 * full-width dated rectangle flow on row 2. Issue-oriented — clean rows dim, the
 * "Issues only" toggle hides healthy completed flows. The §3.3 read-receipt
 * honesty rule is rendered verbatim from the server-derived display state
 * ("Delivered — no read receipt returned", never a confident "unread").
 *
 * Inbound is Phase 2 (the Comms Agent) — the §11 Inbound sub-tab is specified but
 * NOT built here.
 *
 * List controls: the catalogue FilterSearch + FilterPills are reused (the sibling
 * Flow Validator tab on this same page imports the same controls — forking a
 * second copy within one surface would split one page's controls in two). The
 * two-row card list + sortable headers below ARE the purpose-built surface
 * component (§9 Rule 1 — the bespoke part lives here, not a shared table).
 *
 * ZONES (Jeff's rule, 11 October 2026, confirmed 13 October 2026): read-only ops boards are
 * ZONE-AGNOSTIC. Every zone sends its own email from its own SES and keeps its own EmailComm
 * table, so this board fans the read out to every zone from the browser (`fanOutZones`), tags
 * each row with its zone (the Zone column), always shows a per-zone legend over the unfiltered
 * rows, and offers a zone filter that DEFAULTS to "All zones" whatever the "Working in" zone is
 * — the working zone governs edits, not reads. A zone that does not respond is named in the
 * legend and in a per-zone notice; the other zone's rows still render (fail honestly).
 * Standard: Docs/Technical/admin-portal-ui-standards.md § "Read-only ops boards are zone-agnostic".
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterSearch } from '../../components/FilterSearch';
import { FilterPills, type FilterPillGroup } from '../../components/FilterPills';
import { civilDayOf, formatClock24, useEffectiveTimeZone } from '../../lib/time-format';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { ZoneCell, ZoneFanoutWarning, ZoneFilter } from '../../components/ZoneIndicators';
import { ZoneBoardLegend } from '../../components/ZoneBoardLegend';
import { useZoneReadReport } from '../../components/ZoneContext';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type ZoneResult } from '../../lib/zones';
import {
  ALL_ZONES, countInZone, matchesZoneFilter, mergeZoneRows, sortNewestFirst, zoneBoardLegend,
} from '../../lib/zone-board';
import {
  getOutboundEmails,
  type OutboundEmailRow,
  type EmailTransition,
} from '../../lib/email-validation-api';

// ── Stage filter categories (live-counted pills) ───────────────────────────
type Cat = 'delivered' | 'bounced' | 'complaint' | 'rejected' | 'limbo' | 'sent' | 'closed';
const ALL_CATS: Cat[] = ['delivered', 'bounced', 'complaint', 'rejected', 'limbo', 'sent', 'closed'];
const CAT_LABEL: Record<Cat, string> = {
  delivered: 'Delivered',
  bounced: 'Bounced',
  complaint: 'Complaint',
  rejected: 'Rejected',
  limbo: 'In Limbo',
  sent: 'Sent',
  closed: 'Closed',
};

function categoryOf(row: OutboundEmailRow): Cat {
  switch (row.effectiveStage) {
    case 'Bounced':
      return 'bounced';
    case 'Complaint':
      return 'complaint';
    case 'Rejected':
      return 'rejected';
    case 'Closed':
    case 'Read':
      return 'closed';
    case 'Delivered':
      return 'delivered';
    case 'Sent':
      return row.state.label === 'In Limbo' ? 'limbo' : 'sent';
    default:
      return 'sent';
  }
}

// ── Sorting ────────────────────────────────────────────────────────────────
type SortKey = 'recipient' | 'from' | 'sent' | 'state';
type SortDir = 'asc' | 'desc';

// ── Timestamp formatting (§11 — start full datetime; mid +mm:ss; terminal full) ─
// Time Standard: render the instant in the viewer's effective zone. The dense
// flow-strip rectangles keep the compact dd/mm HH:MM:SS shape without a per-rect
// zone label (same rationale as the chart-tick rule — a label per rectangle is
// too noisy); the effective zone comes from the caller.
function fmtFull(iso: string, tz: string): string {
  const day = civilDayOf(iso, tz);
  if (!day) return iso;
  return `${day.slice(8, 10)}/${day.slice(5, 7)} ${formatClock24(iso, tz, { seconds: true })}`;
}
function fmtDelta(fromIso: string, toIso: string): string {
  const ms = Date.parse(toIso) - Date.parse(fromIso);
  if (ms < 0 || Number.isNaN(ms)) return '—';
  const totalSec = Math.round(ms / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `+${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}
function relAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const TERMINAL = new Set(['Bounced', 'Complaint', 'Rejected', 'Closed', 'Read']);
/** Terminal stages that represent a delivery FAILURE (carry an SES/SMTP reason
 *  in `transition.detail`, ADR-074 §3.2) — as opposed to Closed/Read which are
 *  benign terminals. The reason is surfaced beneath the flow strip. */
const FAILURE_STAGES = new Set(['Rejected', 'Bounced', 'Complaint']);

/** Friendly one-line summary of a failure transition's raw SES/SMTP `detail`
 *  (ADR-074 §3.2). Mirrors the error-surfacing standard: a plain, actionable
 *  headline for the operator; the raw detail is shown verbatim beneath it for
 *  diagnosis. Returns null when there is no detail to summarise. */
function friendlyReason(t: EmailTransition): string {
  const d = (t.detail ?? '').toLowerCase();
  if (!d) return `${t.stage} by SES — no reason detail was recorded.`;
  if (d.includes('not authorized') || d.includes('accessdenied') || d.includes('access denied'))
    return 'SES denied the send — the sender role lacks permission for this identity or configuration set.';
  if (d.includes('not verified') || d.includes('email address is not verified'))
    return 'The From or recipient address is not a verified SES identity.';
  if (d.includes('configuration set') && d.includes('does not exist'))
    return 'The SES configuration set referenced by the send does not exist.';
  if (d.includes('suppress'))
    return 'Recipient is on the SES suppression list (from a prior bounce or complaint).';
  if (d.includes('throttl') || d.includes('rate exceeded') || d.includes('maximum sending rate'))
    return 'SES throttled the send — the account sending rate was exceeded.';
  if (d.includes('mailbox') || d.includes('user unknown') || d.includes('no such user'))
    return 'The recipient mailbox does not exist.';
  return `${t.stage} by SES — see the reason below.`;
}
const STAGE_CLASS: Record<string, string> = {
  Queued: 'ev-s-ghost',
  Sent: 'ev-s-on',
  Delivered: 'ev-s-on',
  Read: 'ev-s-success',
  Bounced: 'ev-s-bounce',
  Complaint: 'ev-s-bounce',
  Rejected: 'ev-s-bounce',
  InLimbo: 'ev-s-limbo',
  Closed: 'ev-s-closed',
};

/** Row-2 dated rectangle flow (§11). */
function FlowStrip({ row }: { row: OutboundEmailRow }) {
  const tz = useEffectiveTimeZone();
  const sentAt = row.sentAt ?? row.transitions[0]?.at ?? row.lastEventAt;
  const rects: { label: string; cls: string; date: string; kind: 'full' | 'delta' | 'empty' }[] = [];
  let resends = 0;
  row.transitions.forEach((t: EmailTransition, i) => {
    if (t.event === 'resent') {
      resends += 1;
      rects.push({ label: `↺ Resent (${resends})`, cls: 'ev-s-resend', date: fmtDelta(sentAt, t.at), kind: 'delta' });
      return;
    }
    const isFirst = i === 0;
    const terminal = TERMINAL.has(t.stage);
    const date = isFirst || terminal ? fmtFull(t.at, tz) : fmtDelta(sentAt, t.at);
    rects.push({
      label: t.stage === 'InLimbo' ? 'In Limbo' : t.stage,
      cls: STAGE_CLASS[t.stage] ?? 'ev-s-on',
      date,
      kind: isFirst || terminal ? 'full' : 'delta',
    });
  });
  // Ghosted trailing rectangle for reply-expected flows still awaiting (§11 / §3.3).
  const awaiting = row.expectsResponse && !TERMINAL.has(row.effectiveStage);
  if (awaiting) rects.push({ label: 'Reply', cls: 'ev-s-ghost', date: '—', kind: 'empty' });

  // Surface WHY a send failed (ADR-074 §3.2): the SES/SMTP reason recorded on the
  // terminal-failure transition. Friendly headline + raw detail for diagnosis.
  const failure = row.transitions.find((t) => FAILURE_STAGES.has(t.stage));

  return (
    <>
      <div className="ev-flow">
        {rects.map((r, i) => (
          <span key={i} className="ev-stagewrap">
            {i > 0 && <span className="ev-arr">→</span>}
            <span className="ev-stagecol">
              <span className={`ev-stagedate ev-stagedate-${r.kind}`}>{r.date}</span>
              <span className={`ev-stage ${r.cls}`}>{r.label}</span>
            </span>
          </span>
        ))}
      </div>
      {failure && (
        <div className="ev-reason" role="note">
          <span className="ev-reason-head">{failure.stage}: {friendlyReason(failure)}</span>
          {failure.detail && <span className="ev-reason-raw" title={failure.detail}>{failure.detail}</span>}
        </div>
      )}
    </>
  );
}

const EV_LEGEND_COLUMNS = [
  { key: 'emails', label: 'Emails' },
  { key: 'issues', label: 'Issues' },
] as const;

const truncMid = (m: string | null): string => (m ? `${m.slice(0, 4)}…${m.slice(-4)}` : '—');

interface Props {
  /** Whether the operator may read (mutating tier). Mirrors the server gate. */
  canRead: boolean;
  /** Stage 6 wires the PDF export; passed so the topbar button is owned by the page. */
  renderExport?: (rows: OutboundEmailRow[]) => React.ReactNode;
}

export function EmailValidationOutbound({ canRead, renderExport }: Props) {
  const tz = useEffectiveTimeZone();
  const toast = useToast();
  const [emails, setEmails] = useState<OutboundEmailRow[]>([]);
  const [results, setResults] = useState<ZoneResult<OutboundEmailRow>[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Read-only board — "All zones" by default, whatever the Working-in zone (it governs edits).
  const [zoneFilter, setZoneFilter] = useState(ALL_ZONES);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<Cat>>(new Set(ALL_CATS));
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('sent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // One request per zone, from this browser (ADR-120 Amendment 2) — never a server merge.
      const res = await fanOutZones(async (zone) => (await getOutboundEmails(zone)).emails);
      // Kept even when every zone failed, so the legend names each zone "Did not respond".
      setResults(res);
      // Every zone failing is a failed read, not an empty board.
      throwIfAllZonesFailed(res);
      setEmails(sortNewestFirst(mergeZoneRows(res), (e) => e.sentAt ?? e.transitions[0]?.at ?? e.lastEventAt));
      const failed = failedZoneLabels(res);
      if (failed.length) {
        toast.push({
          variant: 'error',
          title: 'Some email journeys could not be loaded',
          detail: `${failed.join(' and ')} did not respond — its emails are missing from this list.`,
        });
      }
    } catch (err) {
      setEmails([]);
      setError(true);
      toast.push({ variant: 'error', ...toastFromError(err, 'Could not load email journeys') });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  // Per-zone legend over the UNFILTERED rows, so it reads the same whatever the filter shows.
  const legend = useMemo(
    () => zoneBoardLegend(results, {
      counts: (code) => ({
        emails: countInZone(emails, code),
        issues: countInZone(emails, code, (e) => e.state.isIssue),
      }),
    }),
    [emails, results],
  );
  const failedZones = useMemo(() => (results ? failedZoneLabels(results) : []), [results]);
  // ADR-123 Decision 1 — the navigation bar names the zones on this board and their counts.
  useZoneReadReport({ rows: loading ? null : emails, failedZones, filter: zoneFilter, scope: 'Email Validation' });

  // Everything below the legend is zone-filtered.
  const zoneRows = useMemo(() => emails.filter((e) => matchesZoneFilter(e, zoneFilter)), [emails, zoneFilter]);

  const counts = useMemo(() => {
    const c = Object.fromEntries(ALL_CATS.map((k) => [k, 0])) as Record<Cat, number>;
    for (const e of zoneRows) c[categoryOf(e)] += 1;
    return c;
  }, [zoneRows]);

  const pills: FilterPillGroup<Cat>[] = ALL_CATS.map((k) => ({ key: k, label: CAT_LABEL[k], count: counts[k] }));

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (e: OutboundEmailRow) =>
      !q ||
      e.recipient.toLowerCase().includes(q) ||
      e.subject.toLowerCase().includes(q) ||
      e.friendlyId.toLowerCase().includes(q);
    let rows = zoneRows.filter((e) => selected.has(categoryOf(e)) && matches(e));
    if (issuesOnly) rows = rows.filter((e) => e.state.isIssue);
    const cmp = (a: OutboundEmailRow, b: OutboundEmailRow): number => {
      let av: string;
      let bv: string;
      switch (sortKey) {
        case 'recipient': av = a.recipient; bv = b.recipient; break;
        case 'from': av = a.fromAddress; bv = b.fromAddress; break;
        case 'state': av = a.state.label; bv = b.state.label; break;
        case 'sent':
        default: av = a.sentAt ?? a.transitions[0]?.at ?? ''; bv = b.sentAt ?? b.transitions[0]?.at ?? ''; break;
      }
      const r = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? r : -r;
    };
    return [...rows].sort(cmp);
  }, [zoneRows, search, selected, issuesOnly, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'sent' ? 'desc' : 'asc');
    }
  };
  const arrow = (k: SortKey) => (k === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  if (!canRead) {
    return (
      <div className="error-banner">
        Email Validation is restricted to Nexa Support, Nexa Admin, and Super Admin roles.
      </div>
    );
  }

  return (
    <div className="ev-outbound">
      <div className="ev-topbar">
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search recipient, subject or flow ID…"
          ariaLabel="Search outbound emails"
        />
        <ZoneFilter value={zoneFilter} onChange={setZoneFilter} className="zone-board-filter" />
        {renderExport?.(visible)}
      </div>

      {/* Per-zone legend — ALWAYS visible, whatever the filter. A zone that did not respond is a
          row with that status, never a missing row. */}
      <ZoneBoardLegend
        rows={legend}
        columns={EV_LEGEND_COLUMNS}
        testId="ev-zone-legend"
        ariaLabel="Outbound emails by zone"
        loading={loading && !results}
      />
      <ZoneFanoutWarning failedZones={failedZones} noun="outbound emails" onRetry={() => void load()} />

      <FilterPills groups={pills} selected={selected} onChange={setSelected} ariaLabel="Filter by stage" />

      <label className="ev-issue-only">
        <input type="checkbox" checked={issuesOnly} onChange={(e) => setIssuesOnly(e.target.checked)} />
        Issues only — hide healthy completed flows
      </label>

      {loading && (
        <div className="ev-loading" role="status" aria-live="polite" data-testid="ev-loading">
          <p className="ev-loading-text">Loading email journeys from every zone…</p>
          {[0, 1, 2].map((i) => (
            <div className="ev-skel-card" key={i} aria-hidden="true">
              <div className="ev-skel-bar" style={{ width: '55%' }} />
              <div className="ev-skel-bar" />
            </div>
          ))}
        </div>
      )}
      {!loading && error && (
        <p className="muted" data-testid="ev-load-failed">
          No zone answered, so there are no email journeys to show — use Retry above.
        </p>
      )}
      {!loading && !error && selected.size === 0 && (
        <p className="muted">No stage filters selected — pick a pill above to see flows.</p>
      )}
      {!loading && !error && selected.size > 0 && visible.length === 0 && (
        <p className="muted">No outbound emails match.</p>
      )}

      {!loading && !error && visible.length > 0 && (
        <>
          <div className="ev-colhead">
            <span>Zone</span>
            <button type="button" className="ev-colbtn" onClick={() => toggleSort('recipient')}>Recipient{arrow('recipient')}</button>
            <button type="button" className="ev-colbtn" onClick={() => toggleSort('from')}>From{arrow('from')}</button>
            <span>Flow ID</span>
            <span>SES MsgID</span>
            <span>Workflow</span>
            <button type="button" className="ev-colbtn" onClick={() => toggleSort('sent')}>Sent{arrow('sent')}</button>
            <button type="button" className="ev-colbtn" onClick={() => toggleSort('state')}>State{arrow('state')}</button>
            <span>Last</span>
          </div>

          {visible.map((row) => (
            <div key={`${row.zone ?? ''}:${row.id}`} className={`ev-card${row.state.isIssue ? '' : ' ev-card-quiet'}`} data-zone={row.zone}>
              <div className="ev-row1">
                <div className="ev-zone" data-testid="ev-zone-cell"><ZoneCell zone={row.zone} /></div>
                <div className="ev-recip" title={row.recipient}>{row.recipient}</div>
                <div className="ev-addr" title={row.fromAddress}>{row.fromAddress}</div>
                <div><span className="ev-fid">{row.friendlyId}</span></div>
                <div className="ev-mid" title={row.sesMessageId ?? ''}>{truncMid(row.sesMessageId)}</div>
                <div><span className={`ev-wf ev-wf-${row.flowType}`}>{row.workflow}</span></div>
                <div className="ev-sent">{row.sentAt ? fmtFull(row.sentAt, tz) : '—'}</div>
                <div className={`ev-state ev-state-${row.state.tone}`}>{row.state.label}</div>
                <div className="ev-last">{relAgo(row.lastEventAt)}</div>
              </div>
              <div className="ev-row2">
                <FlowStrip row={row} />
                {row.expectsResponse && !TERMINAL.has(row.effectiveStage) && (
                  <div className="ev-awnote">
                    Awaiting read receipt — renders “no open signal”, never a confident “unread”, never alerts on its own.
                  </div>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
